/**
 * ChatbotLibro - Componente de chatbot flotante para consultas sobre un libro
 * Solo visible para el usuario rcr@caze.com
 */

import React, { useState, useRef, useEffect, useContext, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { UserContext } from '../../services/user';
import { sendChatMessage, generateDiagramCode, executeQuickActionWithDocumentoIA, getActionOutputType, getStructuredType, getActionModel, isChatbotErrorFallback, esRespuestaRechazada, MODELS, DEFAULT_MODEL } from './chatService';
import { detectDiagramType, renderMermaid, parseMindElixirData } from './diagramService';
import DiagramRenderer from './DiagramRenderer';
import { normalizarPresentacion, exportarPresentacionPptx, MAX_DIAPOSITIVAS } from './presentacionPptx';
import { generateImage, generateAudio, generatePodcast, getUMADetail, getChatbotCacheEntry, saveChatbotCacheEntry, getLibroVisor } from '../../services/api';
import { getQuickActions } from '../../services/Api/ChatbotQuickActions';
import { getQuickActionCategories } from '../../services/Api/ChatbotQuickActionCategories';
import { getNiaLibro } from '../../services/Api/NiaLibros';
import { organizarContenidosVisorEscaparate } from '../../utils/NewPatchUtils';
import { ROLES } from '../roles';
import { contieneLenguajeOfensivo } from '../palabrasBloqueadas';
import './ChatbotLibro.css';
import chatbotIcon from './chatbot-icon2.png';

const ALLOWED_EMAIL = 'rcr@caze.com';

const ChatIcon = () => (
  <img src={chatbotIcon} alt="Chatbot" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
);

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const ExpandIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
  </svg>
);

const MinimizeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
  </svg>
);

const SendIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
  </svg>
);

const MicrophoneIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
  </svg>
);

const StopRecordingIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="12" r="8" />
  </svg>
);

const BookIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
  </svg>
);

const ExportIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0l-4.5-4.5M12 16.5V3" />
  </svg>
);

const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
  </svg>
);

const ClipboardIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
  </svg>
);

const RegenerateIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
  </svg>
);


const ModelDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const selected = MODELS.find(m => m.value === value) || MODELS[0];

  const handleOpen = () => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuStyle({
        position: 'fixed',
        bottom: window.innerHeight - rect.top + 6,
        right: window.innerWidth - rect.right,
      });
    }
    setOpen(o => !o);
  };

  useEffect(() => {
    const handleClick = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="chatbot-model-dropdown" ref={wrapperRef}>
      <button ref={triggerRef} className="chatbot-model-trigger" onClick={handleOpen} type="button">
        {selected.label}
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </button>
      {open && (
        <div className="chatbot-model-menu" style={menuStyle}>
          {MODELS.map(m => (
            <button
              key={m.value}
              className={`chatbot-model-option ${m.value === value ? 'chatbot-model-option-active' : ''}`}
              onClick={() => { onChange(m.value); setOpen(false); }}
              type="button"
            >
              <span className="chatbot-model-option-label">{m.label}</span>
              <span className="chatbot-model-option-desc">{m.desc}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Menú de Categorías para el toolbar inferior. Agrupa las acciones con
// location === 'chat'. Usa posición fija (como ModelDropdown) porque el toolbar
// vive dentro de un contenedor con overflow:hidden que recortaría el popover.
const CategoriesMenu = ({ groups, onPick }) => {
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [menuStyle, setMenuStyle] = useState({});
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);

  const selected = groups.find(g => g.id === selectedId) || null;

  const handleOpen = () => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuStyle({
        position: 'fixed',
        bottom: window.innerHeight - rect.top + 8,
        left: rect.left,
      });
    }
    setSelectedId(null);
    setOpen(o => !o);
  };

  useEffect(() => {
    const handleClick = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const pick = (action) => { setOpen(false); setSelectedId(null); onPick(action); };

  return (
    <div className="chatbot-categories" ref={wrapperRef}>
      <button
        ref={triggerRef}
        className={`chatbot-toolbar-btn${open ? ' is-active' : ''}`}
        onClick={handleOpen}
        title="Categorías"
        type="button"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.6} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
        </svg>
        <span>Categorías</span>
      </button>
      {open && (
        <div className="chatbot-categories-panel" style={menuStyle}>
          {selected ? (
            <>
              <button className="chatbot-categories-back" onClick={() => setSelectedId(null)} type="button">‹ Categorías</button>
              <div className="chatbot-categories-title">{selected.name}</div>
              <div className="chatbot-categories-actions">
                {selected.actions.map((action, index) => (
                  <button
                    key={action.id || action._id || index}
                    className={`chatbot-quick-action-btn ${action.placeholder ? 'chatbot-quick-action-placeholder' : ''}`}
                    onClick={() => pick(action)}
                    title={action.placeholder ? 'Próximamente' : action.label}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="chatbot-categories-list">
              {groups.map(g => (
                <button key={g.id} className="chatbot-categories-item" onClick={() => setSelectedId(g.id)} type="button">
                  <span>{g.name}</span>
                  <span className="chatbot-categories-count">{g.actions.length}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Botones de acciones rápidas con configuración por rol y contexto.
// Las queries deben ser cortas y enfocadas en el QUÉ; el CÓMO (formato, tono,
// estructura de audio/podcast) lo maneja el system prompt en chatService.js.
const QUICK_ACTIONS = [
  // === ALUMNO ===
  {
    id: 'resumen',
    label: 'Resumen en texto',
    queryLesson: 'Genera un resumen de esta lección con los puntos más importantes',
    queryBook: 'Genera un resumen del libro con los temas y conceptos principales',
    roles: ['ALUMNO'],
    enabled: true,
  },
  {
    id: 'audio',
    label: 'Resumen en audio',
    queryLesson: 'Genera un resumen de esta lección',
    queryBook: 'Genera un resumen del libro',
    roles: ['ALUMNO'],
    enabled: true,
    isAudio: true,
  },
  {
    id: 'podcast',
    label: 'Podcast',
    queryLesson: 'Genera un podcast conversacional sobre esta lección',
    queryBook: 'Genera un podcast conversacional sobre los temas del libro',
    roles: ['ALUMNO'],
    enabled: true,
    isPodcast: true,
  },
  {
    id: 'mapa-mental',
    label: 'Mapa mental',
    queryLesson: 'Genera un mapa mental de esta lección',
    queryBook: 'Genera un mapa mental del libro',
    roles: ['ALUMNO'],
    enabled: true,
    isDiagram: true,
  },
  {
    id: 'imagen',
    label: 'Imagen ilustrativa',
    queryLesson: 'Genera una imagen ilustrativa sobre esta lección',
    queryBook: 'Genera una imagen ilustrativa sobre los temas del libro',
    roles: ['ALUMNO'],
    enabled: true,
    isImage: true,
  },
  {
    id: 'actividades-alumno',
    label: 'Actividades para practicar',
    queryLesson: 'Genera actividades para practicar los temas de esta lección',
    queryBook: 'Genera actividades para practicar los temas del libro',
    roles: ['ALUMNO'],
    enabled: false,
    placeholder: true,
  },
  // === PROFESOR ===
  {
    id: 'resumen-profesor',
    label: 'Resumen en texto',
    queryLesson: 'Genera un resumen de esta lección con los puntos más importantes',
    queryBook: 'Genera un resumen del libro con los temas y conceptos principales',
    roles: ['PROFESOR'],
    enabled: true,
  },
  {
    id: 'audio-profesor',
    label: 'Resumen en audio',
    queryLesson: 'Genera un resumen de esta lección',
    queryBook: 'Genera un resumen del libro',
    roles: ['PROFESOR'],
    enabled: true,
    isAudio: true,
  },
  {
    id: 'podcast-profesor',
    label: 'Podcast',
    queryLesson: 'Genera un podcast conversacional sobre esta lección',
    queryBook: 'Genera un podcast conversacional sobre los temas del libro',
    roles: ['PROFESOR'],
    enabled: true,
    isPodcast: true,
  },
  {
    id: 'mapa-mental-profesor',
    label: 'Mapa mental',
    queryLesson: 'Genera un mapa mental de esta lección',
    queryBook: 'Genera un mapa mental del libro',
    roles: ['PROFESOR'],
    enabled: true,
    isDiagram: true,
  },
  {
    id: 'imagen-profesor',
    label: 'Imagen ilustrativa',
    queryLesson: 'Genera una imagen ilustrativa sobre esta lección',
    queryBook: 'Genera una imagen ilustrativa sobre los temas del libro',
    roles: ['PROFESOR'],
    enabled: true,
    isImage: true,
  },
  {
    id: 'actividades',
    label: 'Actividades para clase',
    queryLesson: 'Genera actividades para trabajar esta lección con los alumnos',
    queryBook: 'Genera actividades para trabajar los temas del libro en clase',
    roles: ['PROFESOR'],
    enabled: false,
    placeholder: true,
  },
  {
    id: 'examen',
    label: 'Cuestionario/Examen',
    queryLesson: 'Crea un cuestionario sobre esta lección para evaluar a los alumnos',
    queryBook: 'Crea un cuestionario sobre los contenidos del libro',
    roles: ['PROFESOR'],
    enabled: false,
    placeholder: true,
  },
];

// Función para filtrar quick actions según rol y contexto.
// Ya no se usa: las acciones vienen solo de la API (ver sourceQuickActions). Se
// conserva junto con QUICK_ACTIONS como referencia del formato de una acción.
// eslint-disable-next-line no-unused-vars
const getFilteredQuickActions = (role, isLesson, lessonName = '', isBookView = false) => {
  return QUICK_ACTIONS
    .filter(action => action.roles.includes(role))
    .map(action => ({
      ...action,
      query: isLesson 
        ? action.queryLesson.replace('esta lección', `la lección "${lessonName}"`)
        : isBookView
        ? action.queryBook.replace('del libro', 'de este libro')
        : action.queryBook
    }));
};

// ---------------------------------------------------------------------------
// Quick Actions estructuradas (outputType === 'structured')
// ---------------------------------------------------------------------------

// Parseo defensivo de la respuesta JSON del modelo. Limpia fences markdown
// (```json ... ```) y, si JSON.parse falla, intenta extraer el primer bloque
// { ... }. Devuelve { data, error }; nunca lanza.
const safeParseStructuredResponse = (raw) => {
  if (!raw || typeof raw !== 'string') return { data: null, error: 'empty' };
  const cleaned = raw.trim()
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/\s*```$/i, '')
    .trim();
  try {
    return { data: JSON.parse(cleaned), error: null };
  } catch (err) {
    const start = cleaned.indexOf('{');
    const end = cleaned.lastIndexOf('}');
    if (start !== -1 && end > start) {
      try {
        return { data: JSON.parse(cleaned.slice(start, end + 1)), error: null };
      } catch (err2) {
        return { data: null, error: err2.message };
      }
    }
    return { data: null, error: err.message };
  }
};

// Coacciones defensivas: el JSON del modelo puede venir incompleto.
const asArray = (v) => (Array.isArray(v) ? v : []);
const asText = (v) => (v == null ? '' : typeof v === 'string' ? v : String(v));

const renderAdvertencias = (advertencias) => {
  const arr = asArray(advertencias);
  if (arr.length === 0) return null;
  return (
    <div className="chatbot-struct-warnings">
      {arr.map((a, i) => <p key={i}>⚠️ {asText(a)}</p>)}
    </div>
  );
};

const renderListSection = (title, items, ordered = false) => {
  const arr = asArray(items);
  if (arr.length === 0) return null;
  const List = ordered ? 'ol' : 'ul';
  return (
    <div className="chatbot-struct-section">
      <p className="chatbot-struct-section-title">{title}</p>
      <List className="chatbot-struct-list">
        {arr.map((it, i) => <li key={i}>{asText(it)}</li>)}
      </List>
    </div>
  );
};

const renderQuiz = (data) => {
  const preguntas = asArray(data.preguntas);
  return (
    <div className="chatbot-struct chatbot-struct-quiz">
      {data.titulo && <h3 className="chatbot-struct-title">{asText(data.titulo)}</h3>}
      {data.instrucciones && <p className="chatbot-struct-instructions">{asText(data.instrucciones)}</p>}
      <ol className="chatbot-struct-questions">
        {preguntas.map((p, i) => {
          const opciones = asArray(p.opciones);
          return (
            <li key={p.id || i} className="chatbot-struct-question">
              <p className="chatbot-struct-question-text">{asText(p.pregunta)}</p>
              {opciones.length > 0 && (
                <ul className="chatbot-struct-options">
                  {opciones.map((op, j) => <li key={j}>{asText(op)}</li>)}
                </ul>
              )}
              {asText(p.respuestaCorrecta) && (
                <p className="chatbot-struct-answer"><strong>Respuesta:</strong> {asText(p.respuestaCorrecta)}</p>
              )}
              {p.retroalimentacion && (
                <p className="chatbot-struct-feedback">{asText(p.retroalimentacion)}</p>
              )}
            </li>
          );
        })}
      </ol>
      {renderAdvertencias(data.advertencias)}
    </div>
  );
};

const renderActivity = (data) => (
  <div className="chatbot-struct chatbot-struct-activity">
    {data.titulo && <h3 className="chatbot-struct-title">{asText(data.titulo)}</h3>}
    {data.objetivo && <p><strong>Objetivo:</strong> {asText(data.objetivo)}</p>}
    {data.duracionSugerida && <p><strong>Duración:</strong> {asText(data.duracionSugerida)}</p>}
    {renderListSection('Materiales', data.materiales)}
    {renderListSection('Instrucciones', data.instrucciones, true)}
    {renderListSection('Criterios de éxito', data.criteriosExito)}
    {renderListSection('Variantes', data.variantes)}
    {renderAdvertencias(data.advertencias)}
  </div>
);

const renderRubric = (data) => {
  const criterios = asArray(data.criterios);
  return (
    <div className="chatbot-struct chatbot-struct-rubric">
      {data.titulo && <h3 className="chatbot-struct-title">{asText(data.titulo)}</h3>}
      {criterios.map((c, i) => {
        const niveles = asArray(c.niveles);
        return (
          <div key={i} className="chatbot-struct-criterio">
            <p className="chatbot-struct-section-title">{asText(c.criterio)}</p>
            {niveles.length > 0 && (
              <table className="chatbot-struct-table">
                <thead>
                  <tr><th>Nivel</th><th>Descripción</th><th>Puntaje</th></tr>
                </thead>
                <tbody>
                  {niveles.map((n, j) => (
                    <tr key={j}>
                      <td>{asText(n.nivel)}</td>
                      <td>{asText(n.descripcion)}</td>
                      <td>{asText(n.puntaje)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
      {asText(data.puntajeTotal) && (
        <p className="chatbot-struct-total"><strong>Puntaje total:</strong> {asText(data.puntajeTotal)}</p>
      )}
      {renderAdvertencias(data.advertencias)}
    </div>
  );
};

const renderFlashcards = (data) => {
  const tarjetas = asArray(data.tarjetas);
  return (
    <div className="chatbot-struct chatbot-struct-flashcards">
      {data.titulo && <h3 className="chatbot-struct-title">{asText(data.titulo)}</h3>}
      <div className="chatbot-struct-cards">
        {tarjetas.map((t, i) => (
          <div key={i} className="chatbot-struct-card">
            <div className="chatbot-struct-card-front">{asText(t.frente)}</div>
            <div className="chatbot-struct-card-back">{asText(t.reverso)}</div>
            {t.pista && <div className="chatbot-struct-card-hint">Pista: {asText(t.pista)}</div>}
          </div>
        ))}
      </div>
      {renderAdvertencias(data.advertencias)}
    </div>
  );
};

const renderLessonPlan = (data) => {
  const campos = [
    ['Objetivo', data.objetivo],
    ['Inicio', data.inicio],
    ['Desarrollo', data.desarrollo],
    ['Cierre', data.cierre],
    ['Evaluación', data.evaluacion],
    ['Tiempo estimado', data.tiempoEstimado],
  ];
  return (
    <div className="chatbot-struct chatbot-struct-lessonplan">
      {data.titulo && <h3 className="chatbot-struct-title">{asText(data.titulo)}</h3>}
      {campos.map(([label, val]) => (asText(val) ? (
        <p key={label}><strong>{label}:</strong> {asText(val)}</p>
      ) : null))}
      {renderListSection('Materiales', data.materiales)}
      {renderAdvertencias(data.advertencias)}
    </div>
  );
};

const renderGenericStructured = (data) => {
  const elementos = asArray(data.elementos);
  return (
    <div className="chatbot-struct chatbot-struct-generic">
      {data.titulo && <h3 className="chatbot-struct-title">{asText(data.titulo)}</h3>}
      {elementos.length > 0 && (
        <ul className="chatbot-struct-list">
          {elementos.map((e, i) => <li key={i}>{typeof e === 'object' ? JSON.stringify(e) : asText(e)}</li>)}
        </ul>
      )}
      {renderAdvertencias(data.advertencias)}
    </div>
  );
};

// Normaliza para comparar respuestas (sin acentos, minúsculas, sin espacios extra).
const normalizeAnswer = (s) =>
  asText(s).trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

// Quiz interactivo: el alumno responde dentro del chat. Las opciones se pueden
// seleccionar y la respuesta correcta/retroalimentación permanecen ocultas hasta
// que pulsa "Revisar respuestas"; entonces se califica y se revelan los aciertos.
const StructuredQuiz = ({ data }) => {
  const preguntas = asArray(data.preguntas);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const keyOf = (p, i) => p.id || `q-${i}`;
  const setAnswer = (key, value) => { if (!submitted) setAnswers(prev => ({ ...prev, [key]: value })); };
  const isCorrect = (p, i) => {
    const given = answers[keyOf(p, i)];
    if (given == null || given === '') return false;
    return normalizeAnswer(given) === normalizeAnswer(p.respuestaCorrecta);
  };

  const score = preguntas.reduce((acc, p, i) => acc + (isCorrect(p, i) ? 1 : 0), 0);
  const answeredCount = preguntas.filter((p, i) => {
    const v = answers[keyOf(p, i)];
    return v != null && v !== '';
  }).length;

  return (
    <div className="chatbot-struct chatbot-struct-quiz">
      {data.titulo && <h3 className="chatbot-struct-title">{asText(data.titulo)}</h3>}
      {data.instrucciones && <p className="chatbot-struct-instructions">{asText(data.instrucciones)}</p>}
      <ol className="chatbot-struct-questions">
        {preguntas.map((p, i) => {
          const key = keyOf(p, i);
          const given = answers[key] ?? '';
          const correcta = submitted && isCorrect(p, i);
          const opciones = asArray(p.opciones);
          const opcionesRender = opciones.length > 0
            ? opciones
            : asText(p.tipo) === 'verdadero_falso' ? ['Verdadero', 'Falso'] : [];

          return (
            <li key={key} className="chatbot-struct-question">
              <p className="chatbot-struct-question-text">{asText(p.pregunta)}</p>

              {opcionesRender.length > 0 ? (
                <div className="chatbot-quiz-options">
                  {opcionesRender.map((op, j) => {
                    const opStr = asText(op);
                    const selected = normalizeAnswer(given) === normalizeAnswer(opStr);
                    const esCorrecta = submitted && normalizeAnswer(opStr) === normalizeAnswer(p.respuestaCorrecta);
                    const esSeleccionErrada = submitted && selected && !esCorrecta;
                    return (
                      <label
                        key={j}
                        className={`chatbot-quiz-option${selected ? ' is-selected' : ''}${esCorrecta ? ' is-correct' : ''}${esSeleccionErrada ? ' is-incorrect' : ''}`}
                      >
                        <input
                          type="radio"
                          name={key}
                          value={opStr}
                          checked={selected}
                          disabled={submitted}
                          onChange={() => setAnswer(key, opStr)}
                          style={{ accentColor: '#162e5b' }}
                        />
                        <span>{opStr}</span>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <input
                  type="text"
                  className="chatbot-quiz-input"
                  placeholder="Escribe tu respuesta..."
                  value={given}
                  disabled={submitted}
                  onChange={(e) => setAnswer(key, e.target.value)}
                />
              )}

              {submitted && (
                <div className={`chatbot-quiz-result ${correcta ? 'ok' : 'no'}`}>
                  <span>{correcta ? '✓ Correcto' : '✗ Incorrecto'}</span>
                  {!correcta && asText(p.respuestaCorrecta) && (
                    <span> · Respuesta: {asText(p.respuestaCorrecta)}</span>
                  )}
                  {p.retroalimentacion && (
                    <p className="chatbot-struct-feedback">{asText(p.retroalimentacion)}</p>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ol>

      {!submitted ? (
        <button
          type="button"
          className="chatbot-quiz-submit"
          disabled={answeredCount === 0}
          onClick={() => setSubmitted(true)}
        >
          Revisar respuestas
        </button>
      ) : (
        <div className="chatbot-quiz-score">
          <span>Obtuviste {score} de {preguntas.length} {preguntas.length === 1 ? 'punto' : 'puntos'}</span>
          <button
            type="button"
            className="chatbot-quiz-retry"
            onClick={() => { setSubmitted(false); setAnswers({}); }}
          >
            Intentar de nuevo
          </button>
        </div>
      )}
      {renderAdvertencias(data.advertencias)}
    </div>
  );
};

// Presentación: el visor recorre las mismas láminas que va a tener el .pptx
// (portada + hasta MAX_DIAPOSITIVAS de contenido), así lo que se ve en el chat y
// lo que se descarga no se separan nunca. El archivo se arma en el navegador con
// pptxgenjs, que se carga sólo al pulsar Descargar.
const PresentationViewer = ({ data }) => {
  const presentacion = useMemo(() => normalizarPresentacion(data), [data]);
  const { titulo, subtitulo, diapositivas, advertencias, recortada } = presentacion;

  const laminas = useMemo(() => [
    { tipo: 'portada', titulo, subtitulo },
    ...diapositivas.map(d => ({ tipo: 'contenido', ...d })),
  ], [titulo, subtitulo, diapositivas]);

  const [indice, setIndice] = useState(0);
  const [verNotas, setVerNotas] = useState(false);
  const [descargando, setDescargando] = useState(false);
  const [errorDescarga, setErrorDescarga] = useState('');
  const [enPantallaCompleta, setEnPantallaCompleta] = useState(false);
  const contenedorRef = useRef(null);

  // El tamaño en pantalla completa se resuelve con una clase propia y no con la
  // pseudo-clase :fullscreen: Safari sólo entiende :-webkit-full-screen y la regla
  // depende de que el navegador la aplique al elemento correcto. Con la clase, el
  // CSS es un selector normal y el estado lo manda este listener, que además cubre
  // la salida con Escape (que no pasa por el botón).
  useEffect(() => {
    const sincronizar = () => {
      const el = contenedorRef.current;
      const activo = document.fullscreenElement || document.webkitFullscreenElement;
      setEnPantallaCompleta(Boolean(el && activo === el));
    };
    document.addEventListener('fullscreenchange', sincronizar);
    document.addEventListener('webkitfullscreenchange', sincronizar);
    return () => {
      document.removeEventListener('fullscreenchange', sincronizar);
      document.removeEventListener('webkitfullscreenchange', sincronizar);
    };
  }, []);

  const total = laminas.length;
  const actual = laminas[Math.min(indice, total - 1)] || laminas[0];

  const irA = (i) => setIndice(Math.max(0, Math.min(i, total - 1)));

  const handleDescargar = async () => {
    setDescargando(true);
    setErrorDescarga('');
    const { ok, error } = await exportarPresentacionPptx(data);
    if (!ok) setErrorDescarga(error || 'No se pudo generar el archivo.');
    setDescargando(false);
  };

  // Misma mecánica que el visor de diagramas: pantalla completa sobre el propio
  // contenedor, que es lo que sirve para proyectar desde el chat.
  const handlePantallaCompleta = () => {
    const el = contenedorRef.current;
    if (!el) return;
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
      return;
    }
    const pedir = el.requestFullscreen || el.webkitRequestFullscreen;
    // El foco se pierde al entrar y sin él las flechas no avanzan la presentación.
    Promise.resolve(pedir?.call(el))
      .then(() => el.focus())
      .catch(err => console.error('Error al entrar en pantalla completa:', err));
  };

  // Flechas para avanzar: sólo cuando el visor tiene el foco, para no robarle las
  // teclas al campo de texto del chat.
  const handleTeclas = (e) => {
    if (e.key === 'ArrowRight' || e.key === 'PageDown') { e.preventDefault(); irA(indice + 1); }
    if (e.key === 'ArrowLeft' || e.key === 'PageUp') { e.preventDefault(); irA(indice - 1); }
  };

  if (diapositivas.length === 0) {
    return (
      <div className="chatbot-struct chatbot-struct-presentation">
        {titulo && <h3 className="chatbot-struct-title">{titulo}</h3>}
        <p>No se pudo armar la presentación. Intenta de nuevo.</p>
        {renderAdvertencias(advertencias)}
      </div>
    );
  }

  return (
    <div className="chatbot-struct chatbot-struct-presentation">
      {titulo && <h3 className="chatbot-struct-title">{titulo}</h3>}

      <div
        className={`chatbot-pres-escenario${enPantallaCompleta ? ' es-pantalla-completa' : ''}${verNotas ? ' con-notas' : ''}`}
        ref={contenedorRef}
        tabIndex={0}
        onKeyDown={handleTeclas}
      >
        <div className={`chatbot-pres-lamina${actual.tipo === 'portada' ? ' es-portada' : ''}`}>
          {actual.tipo === 'portada' ? (
            <>
              <p className="chatbot-pres-portada-titulo">{actual.titulo || 'Presentación'}</p>
              {actual.subtitulo && <p className="chatbot-pres-portada-subtitulo">{actual.subtitulo}</p>}
            </>
          ) : (
            <>
              <p className="chatbot-pres-lamina-titulo">{actual.titulo}</p>
              {actual.puntos.length > 0 && (
                <ul className="chatbot-pres-puntos">
                  {actual.puntos.map((p, i) => <li key={i}>{p}</li>)}
                </ul>
              )}
            </>
          )}
        </div>

        <div className="chatbot-pres-controles">
          <button type="button" onClick={() => irA(indice - 1)} disabled={indice === 0} title="Anterior">‹</button>
          <span className="chatbot-pres-contador">{indice + 1} / {total}</span>
          <button type="button" onClick={() => irA(indice + 1)} disabled={indice >= total - 1} title="Siguiente">›</button>
          <button
            type="button"
            className={`chatbot-pres-toggle${verNotas ? ' is-active' : ''}`}
            onClick={() => setVerNotas(v => !v)}
            title="Notas del orador"
          >
            Notas
          </button>
          <button type="button" className="chatbot-pres-toggle" onClick={handlePantallaCompleta} title="Pantalla completa">
            {enPantallaCompleta ? 'Salir de pantalla completa' : 'Pantalla completa'}
          </button>
        </div>

        {verNotas && (
          <div className="chatbot-pres-notas">
            {actual.tipo === 'contenido' && actual.notas
              ? actual.notas
              : 'Esta lámina no tiene notas del orador.'}
          </div>
        )}
      </div>

      <div className="chatbot-pres-descarga">
        <button type="button" onClick={handleDescargar} disabled={descargando}>
          {descargando ? 'Generando archivo...' : 'Descargar .pptx'}
        </button>
        {errorDescarga && <span className="chatbot-pres-error">{errorDescarga}</span>}
      </div>

      {recortada && (
        <p className="chatbot-pres-aviso">
          Se muestran las primeras {MAX_DIAPOSITIVAS} diapositivas.
        </p>
      )}
      {renderAdvertencias(advertencias)}
    </div>
  );
};

// Render principal: elige el render según structuredType (o data.tipo). Si el
// tipo es desconocido o faltan campos, cae al render genérico sin romper.
// El quiz es interactivo para alumnos (responden ahí mismo, sin ver la clave);
// el profesor ve la versión con respuestas para revisión.
const renderStructuredContent = (data, structuredType, userRole) => {
  if (!data || typeof data !== 'object') return null;
  const type = structuredType || data.tipo || '';
  switch (type) {
    case 'quiz':       return userRole === 'PROFESOR' ? renderQuiz(data) : <StructuredQuiz data={data} />;
    case 'activity':   return renderActivity(data);
    case 'rubric':     return renderRubric(data);
    case 'flashcards': return renderFlashcards(data);
    case 'lessonPlan': return renderLessonPlan(data);
    case 'presentation': return <PresentationViewer data={data} />;
    default:           return renderGenericStructured(data);
  }
};

const LOGIN_PATHS = ['/', '/change/password', '/verify/email', '/recover/password', '/verify/password', '/permission/denied/password'];

const ChatbotLibro = () => {
  const { user, activeRole } = useContext(UserContext);
  
  // Estado para rastrear la ruta actual (se actualiza con cambios de navegación)
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  
  // Escuchar cambios de ruta
  useEffect(() => {
    const checkPath = () => {
      if (window.location.pathname !== currentPath) {
        setCurrentPath(window.location.pathname);
      }
    };
    
    // Verificar cambios periódicamente (para navegación SPA)
    const interval = setInterval(checkPath, 100);
    
    // También escuchar popstate para navegación con botones del navegador
    window.addEventListener('popstate', checkPath);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('popstate', checkPath);
    };
  }, [currentPath]);
  
  // Obtener rol del usuario (default: ALUMNO)
  const userRole = activeRole || user?.activeRole || 'ALUMNO';

  // Detectar si estamos en login o rutas públicas
  const isLoginPage = LOGIN_PATHS.some(p => p === '/' ? currentPath === '/' : currentPath.startsWith(p));
  const token = window.localStorage.getItem('token') || window.sessionStorage.getItem('token');
  const isBookInformationPage = currentPath.includes('/visor/BookInformation/');

  // Visibilidad por licencia del campus:
  //   Básica   → nadie ve el chatbot --- cambia a --- Todos ven los botones
  //   Estándar → solo PROFESOR --- cambia a --- PROFESOR ve el chatbot, ALUMNO ve botones
  //   Plus     → PROFESOR y ALUMNO --- cambia a --- Igual que estandar
  const licenciaNombre = user?.escuela?.campus?.licencia?.nombre || '';
  const licenciaPermite = (() => {
    if (licenciaNombre === 'Plus') return ['PROFESOR', 'ALUMNO'].includes(userRole);
    if (licenciaNombre === 'Estándar') return userRole === 'PROFESOR';
    return false; // Básica u otras → oculto
  })();

  // Aún falta la configuración de NIA del libro, que se resuelve más abajo
  // (necesita el bookId). Estas condiciones no dependen de ella y hay efectos
  // que las consultan antes de que exista.
  const contextoPermite = !isLoginPage && !!token && isBookInformationPage && licenciaPermite;
  
  // Detectar contexto: lección vs libro (usando localStorage para lección seleccionada)
  const getContext = () => {
    const pathname = currentPath;
    
    // Detectar si estamos en BookInformation (vista de libro específico)
    if (pathname.includes('/visor/BookInformation/')) {
      const bookId = pathname.split('/visor/BookInformation/')[1]?.split('/')[0] || '';
      
      // Verificar si hay una lección seleccionada en localStorage
      const storedLeccion = localStorage.getItem('selectedLeccion');
      if (storedLeccion) {
        const leccion = JSON.parse(storedLeccion);
        return { 
          isLesson: true, 
          lessonName: leccion.titulo || '', 
          lessonId: leccion._id,
          bookId, 
          isBookView: false 
        };
      }
      
      // Si no hay lección seleccionada, estamos en vista de libro
      return { isLesson: false, lessonName: '', lessonId: '', bookId, isBookView: true };
    }
    
    return { isLesson: false, lessonName: '', lessonId: '', bookId: '', isBookView: false };
  };

  const [context, setContext] = useState(getContext());

  // Configuración de NIA del libro abierto. La regla es opt-in: el director
  // editorial habilita libro por libro desde /herramientas/nia-libros, así que
  // mientras no se sepa (null) o el libro no esté habilitado, NIA no se muestra.
  // Un error de red se trata igual que "apagado", por la misma razón que con las
  // acciones rápidas: es preferible no mostrar NIA a mostrarlo donde no toca.
  const [niaHabilitado, setNiaHabilitado] = useState(null);

  useEffect(() => {
    const bookId = context.bookId;
    if (!contextoPermite || !bookId) {
      setNiaHabilitado(null);
      return;
    }
    // Se limpia antes de pedir: al saltar de un libro habilitado a uno que no lo
    // está, sin esto NIA seguiría visible hasta que respondiera la petición.
    setNiaHabilitado(null);
    let cancelado = false;
    getNiaLibro(bookId)
      .then(data => { if (!cancelado) setNiaHabilitado(data?.habilitado === true); })
      .catch(() => { if (!cancelado) setNiaHabilitado(false); });
    return () => { cancelado = true; };
  }, [context.bookId, contextoPermite]);

  const shouldHide = !contextoPermite || niaHabilitado !== true;
  
  // Estado para la lección seleccionada desde el menú lateral
  const [selectedLeccion, setSelectedLeccion] = useState(() => {
    const stored = localStorage.getItem('selectedLeccion');
    return stored ? JSON.parse(stored) : null;
  });

  // Datos reales de la UMA obtenidos desde la API
  const [lessonUMAData, setLessonUMAData] = useState(null);

  // Datos ligeros del libro actual (título + nombres de lecciones)
  const [bookData, setBookData] = useState(null);

  // IDs de UMA de todas las lecciones del libro actual, para el resumen del libro con Documento IA
  const [bookLeccionesData, setBookLeccionesData] = useState([]);
  // De qué libro son bookData y bookLeccionesData. Sin esto no hay forma de saber
  // que lo cargado ya no corresponde al libro que el usuario tiene abierto.
  const [bookLeccionesBookId, setBookLeccionesBookId] = useState(null);

  // Recorrer el árbol de contenidos (mismo orden que Table) y extraer las lecciones (nodos con uma) en orden,
  // incluyendo el agrupador (unidad/tema) al que pertenecen
  const flattenLeccionesOrdenadas = (nodos, path = []) => {
    let result = [];
    const ordenados = [...(nodos || [])].sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
    for (const nodo of ordenados) {
      if (nodo.uma?.esEliminado) continue;
      const umaId = typeof nodo.uma === 'object' ? nodo.uma?._id : (typeof nodo.uma === 'string' ? nodo.uma : null);
      if (umaId) {
        const nombre = (typeof nodo.uma === 'object' && nodo.uma && (nodo.uma.descripcion || nodo.uma.titulo)) || nodo.titulo || '';
        if (nombre) result.push({ id: umaId, nombre, agrupadores: path });
      }
      if (nodo.hijos?.length) {
        const nextPath = nodo.titulo ? [...path, nodo.titulo] : path;
        result = result.concat(flattenLeccionesOrdenadas(nodo.hijos, nextPath));
      }
    }
    return result;
  };

  // Obtener todos los contenidos del libro (con UMA IDs) para el resumen del libro, en el mismo orden que Table
  const fetchBookLeccionesData = async (bookId) => {
    if (!bookId) return null;
    try {
      const result = await getLibroVisor(bookId);
      const contenidos = result?.data?.contenidos || [];
      //console.log('[ChatbotLibro] getLibroVisor contenidos →', { total: contenidos.length, contenidos });
      const arbol = organizarContenidosVisorEscaparate(contenidos);
      const seenIds = new Set();
      const leccionesData = [];
      for (const leccion of flattenLeccionesOrdenadas(arbol[0]?.hijos || [])) {
        if (seenIds.has(leccion.id)) continue;
        seenIds.add(leccion.id);
        leccionesData.push(leccion);
      }
      //console.log('[ChatbotLibro] bookLeccionesData →', { total: leccionesData.length, leccionesData });
      // El título y los nombres salen de aquí y no solo del evento 'bookLoaded':
      // ese evento no se repite al saltar de un libro a otro (ver el efecto de abajo).
      const datosLibro = {
        titulo: result?.data?.titulo || '',
        lecciones: [...new Set(leccionesData.map(l => l.nombre).filter(Boolean))],
      };
      setBookLeccionesData(leccionesData);
      setBookData(datosLibro);
      setBookLeccionesBookId(bookId);
      return { leccionesData, bookData: datosLibro };
    } catch (err) {
      console.error('[ChatbotLibro] Error al obtener contenidos del libro:', err);
      setBookLeccionesData([]);
      setBookLeccionesBookId(bookId);
      return null;
    }
  };

  // Función para obtener datos de la UMA desde la API usando el UMA ID
  const fetchLessonUMAData = async (leccion) => {
    const umaId = leccion?.umaId || leccion?._id;
    if (!umaId) return;
    try {
      const result = await getUMADetail(umaId);
      //console.log("getUMADetail result:", result.data);
      if (result?.data) {
        setLessonUMAData(result.data);
      }
    } catch (err) {
      console.error('[ChatbotLibro] Error al obtener datos de la UMA:', err);
      setLessonUMAData(null);
    }
  };

  // Cargar datos de la UMA si hay una lección seleccionada al montar el componente
  useEffect(() => {
    const stored = localStorage.getItem('selectedLeccion');
    if (stored) {
      const leccion = JSON.parse(stored);
      if (leccion?.umaId || leccion?._id) fetchLessonUMAData(leccion);
    }
  }, []);

  // Escuchar eventos de selección/cierre de lección y carga de libro
  useEffect(() => {
    const handleLeccionSelected = (event) => {
      const leccion = event.detail;
      setSelectedLeccion(leccion);
      setContext(getContext());
      fetchLessonUMAData(leccion);
    };

    const handleLeccionClosed = () => {
      localStorage.removeItem('selectedLeccion');
      setSelectedLeccion(null);
      setLessonUMAData(null);
      setContext(getContext());
    };

    const handleBookLoaded = (event) => {
      setBookData(event.detail);
      const bookId = getContext().bookId;
      fetchBookLeccionesData(bookId);
    };

    window.addEventListener('leccionSelected', handleLeccionSelected);
    window.addEventListener('leccionClosed', handleLeccionClosed);
    window.addEventListener('bookLoaded', handleBookLoaded);
    return () => {
      window.removeEventListener('leccionSelected', handleLeccionSelected);
      window.removeEventListener('leccionClosed', handleLeccionClosed);
      window.removeEventListener('bookLoaded', handleBookLoaded);
    };
  }, []);
  
  // Actualizar contexto cuando cambie la ruta o la lección seleccionada
  useEffect(() => {
    setContext(getContext());
    // Limpiar lección seleccionada al cambiar de ruta (volver a portada)
    if (!currentPath.includes('/visor/BookInformation/')) {
      setSelectedLeccion(null);
      setLessonUMAData(null);
      setBookData(null);
      setBookLeccionesData([]);
      setBookLeccionesBookId(null);
      localStorage.removeItem('selectedLeccion');
    }
  }, [currentPath, selectedLeccion]);

  // Cambio de libro sin salir del visor. BookInformation carga el libro en un
  // efecto con dependencias vacías, así que al pasar de /visor/BookInformation/A a
  // /visor/BookInformation/B el componente se reutiliza, el efecto no vuelve a
  // correr y NUNCA se emite 'bookLoaded' para B: el chatbot se quedaba con el
  // contenido de A y respondía sobre el libro anterior.
  useEffect(() => {
    const bookId = context.bookId;
    if (!bookId || bookId === bookLeccionesBookId) return;

    // Veníamos de otro libro: la lección guardada es de aquel, no de este. En el
    // primer montaje (bookLeccionesBookId === null) se respeta, que es el caso de
    // recargar la página estando dentro de una lección.
    if (bookLeccionesBookId !== null) {
      setSelectedLeccion(null);
      setLessonUMAData(null);
      localStorage.removeItem('selectedLeccion');
    }

    setBookData(null);
    setBookLeccionesData([]);
    fetchBookLeccionesData(bookId);
  }, [context.bookId, bookLeccionesBookId]);

  const [remoteQuickActions, setRemoteQuickActions] = useState(null);
  const [remoteCategories, setRemoteCategories] = useState([]);

  // Mapear rol del sistema (ROLES de roles.js) al ROLE_INSTRUCTIONS de chatService
  const getChatRole = () => {
    const rolesConInstrucciones = [
      ROLES.alumno,
      ROLES.profesor,
      ROLES.padre,
      ROLES.coordinador,
      ROLES.plantel,
      ROLES.corporativo,
      ROLES.admin,
      ROLES.operativo,
    ];
    return rolesConInstrucciones.includes(userRole) ? userRole : 'general';
  };
  const chatRole = getChatRole();
  
  // Quick actions del rol, ya filtradas y con el prompt resuelto según el contexto.
  // Solo salen de la API: si el ambiente no tiene ninguna configurada, la lista
  // queda vacía y NIA no se muestra (ver hayAccionesDelRol).
  const sourceQuickActions = (remoteQuickActions || []).filter(a => a.enabled !== false);

  // Una acción se muestra en el contexto actual (lección o libro) solo si el
  // gestor la marcó para ese contexto y además tiene prompt para él: un prompt
  // vacío no se puede ejecutar, así que la acción se oculta. Las acciones
  // anteriores a showInLesson/showInBook no traen los campos: se asumen en true.
  const accionVisibleEnContexto = (a) => (
    context.isLesson
      ? a.showInLesson !== false && !!(a.queryLesson || '').trim()
      : a.showInBook   !== false && !!(a.queryBook   || '').trim()
  );

  // Orden configurado en el gestor para este rol (cada rol tiene el suyo). Las
  // acciones sin posición van al final; entre ellas se conserva el orden de la API
  // porque sort es estable.
  const ordenDelRol = (a) => {
    const valor = a.orden?.[userRole];
    return typeof valor === 'number' ? valor : Number.MAX_SAFE_INTEGER;
  };

  const filteredQuickActions = sourceQuickActions
    .filter(a => (a.roles || []).includes(userRole))
    .filter(accionVisibleEnContexto)
    .sort((a, b) => ordenDelRol(a) - ordenDelRol(b))
    .map(a => ({
      ...a,
      query: context.isLesson
        ? (a.queryLesson || '').replace('esta lección', `la lección "${context.lessonName}"`)
        : (a.queryBook || ''),
    }));

  // NIA no se muestra si el rol no tiene ninguna acción activa en el contexto
  // actual: con acciones solo de lección, NIA no aparece en la portada del libro
  // (y al revés). Mientras la consulta va en camino (null) tampoco: es preferible
  // no pintar el botón a pintarlo y que desaparezca. En producción, sin acciones
  // configuradas, NIA no aparece.
  const hayAccionesDelRol = filteredQuickActions.length > 0;

  // Separar por ubicación: 'top' (barra superior, como hasta ahora) vs 'chat'
  // (área inferior, agrupadas por categoría detrás del botón "Categorías").
  const topQuickActions = filteredQuickActions.filter(a => (a.location || 'top') !== 'chat');
  const chatQuickActions = filteredQuickActions.filter(a => (a.location || 'top') === 'chat');

  // Agrupar las acciones de chat por categoría. Las que no tengan categoría (o
  // cuya categoría ya no exista) caen en un grupo "Otras".
  const chatActionsByCategory = chatQuickActions.reduce((acc, a) => {
    const key = a.categoryId || '__none__';
    (acc[key] = acc[key] || []).push(a);
    return acc;
  }, {});
  const categoryGroups = [
    ...(remoteCategories || [])
      .filter(c => chatActionsByCategory[c._id]?.length)
      .map(c => ({ id: c._id, name: c.name, actions: chatActionsByCategory[c._id] })),
    ...(chatActionsByCategory.__none__?.length
      ? [{ id: '__none__', name: 'Otras', actions: chatActionsByCategory.__none__ }]
      : []),
  ];

  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [selectedModel, setSelectedModel] = useState(DEFAULT_MODEL);
  const [copied, setCopied] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: '¡Hola! Soy tu asistente de contenido. ¿En qué puedo ayudarte?' }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [audioLoading, setAudioLoading] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);

  // Actualizar saludo cuando llegan los datos reales del libro o del usuario
  useEffect(() => {
    const titulo = bookData?.titulo || '';
    if (!titulo) return;
    const nombre = user?.nombres ? `${user.nombres}! ` : '';
    const saludo = userRole === 'ALUMNO'
      ? `¡Hola ${nombre}Soy tu asistente para estudiar el libro "${titulo}". Por favor, selecciona una de las opciones de arriba para comenzar.`
      : `¡Hola ${nombre}Soy tu asistente para consultas sobre el libro "${titulo}". ¿En qué puedo ayudarte?`;
    setMessages([{ role: 'assistant', content: saludo }]);
  }, [bookData, user, userRole]);

  // Verificar si el usuario tiene acceso
  const hasAccess = user?.correoElectronico === ALLOWED_EMAIL;

  // Auto-scroll al último mensaje
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Focus en input al abrir
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // El input crece con el contenido hasta un máximo de 3 líneas; a partir de ahí
  // se queda fijo y hace scroll interno.
  useEffect(() => {
    const campo = inputRef.current;
    if (!campo) return;
    campo.style.height = 'auto';
    const estilos = window.getComputedStyle(campo);
    const alturaLinea = parseFloat(estilos.lineHeight) || 20;
    const relleno = parseFloat(estilos.paddingTop) + parseFloat(estilos.paddingBottom);
    const alturaMaxima = alturaLinea * 3 + relleno;
    campo.style.height = `${Math.min(campo.scrollHeight, alturaMaxima)}px`;
    campo.style.overflowY = campo.scrollHeight > alturaMaxima ? 'auto' : 'hidden';
  }, [inputValue, isOpen]);

  // Cargar quick actions y categorías desde la API. Se consulta aunque el chatbot
  // esté cerrado porque el botón de NIA solo se muestra si el rol tiene alguna
  // acción, y eso hay que saberlo antes de pintarlo.
  // `remoteQuickActions` distingue tres estados: null = todavía no se sabe,
  // [] = se consultó y no hay ninguna, [..] = las que devolvió el backend. Un error
  // de red se trata como "no hay": es preferible no mostrar NIA a mostrarlo con
  // acciones que quizá no existan en ese ambiente.
  useEffect(() => {
    if (!token || !isBookInformationPage || !licenciaPermite) return;
    getQuickActions()
      .then(data => setRemoteQuickActions(Array.isArray(data) ? data : []))
      .catch(() => setRemoteQuickActions([]));
    // El backend de categorías puede no existir aún: degradar a lista vacía.
    getQuickActionCategories()
      .then(data => { if (Array.isArray(data)) setRemoteCategories(data); })
      .catch(() => {});
  }, [token, isBookInformationPage, licenciaPermite]);

  

  const handleSendMessage = async (messageToSend = null) => {
    const trimmedInput = messageToSend || inputValue.trim();
    if (!trimmedInput || isLoading) return;

    // Filtro de lenguaje ofensivo (validación local, antes de llamar a la IA).
    if (contieneLenguajeOfensivo(trimmedInput)) {
      setMessages(prev => [...prev,
        { role: 'user', content: trimmedInput },
        { role: 'assistant', content: 'Cuidemos el lenguaje 🙂. Estoy aquí para ayudarte con preguntas sobre el contenido del libro.' }
      ]);
      setInputValue('');
      return;
    }

    // Agregar mensaje del usuario
    const userMessage = { role: 'user', content: trimmedInput };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // El chat libre es SOLO texto. Audio, podcast, imagen, diagrama y
      // estructurado son exclusivos de los quick actions; aquí respondemos
      // únicamente con texto basado en el contenido (Documento IA → fallback local).
      const response = await sendChatMessage(trimmedInput, messages, chatRole, selectedLeccion, lessonUMAData, bookData, selectedModel, false, false, selectedLeccion?.umaId || selectedLeccion?._id || lessonUMAData?._id, bookLeccionesData);
      setMessages(prev => [...prev, { role: 'assistant', content: response }]);
    } catch (error) {
      console.error('[ChatbotLibro] Error:', error);
      setMessages(prev => [...prev, { 
        role: 'error', 
        content: 'Lo siento, hubo un error al procesar tu mensaje. Por favor intenta de nuevo.' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickAction = async (action) => {
    // Si es un placeholder, mostrar mensaje de "Próximamente"
    if (action.placeholder) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `"${action.label}" estará disponible próximamente. Estamos trabajando en esta funcionalidad.`
      }]);
      return;
    }

    // Ubicación de la acción: las de "abajo" (área de chat) son conversacionales
    // → llevan historial y NO usan caché. Las de "arriba" → usan caché y sin historial.
    const isChatAction = (action.location || 'top') === 'chat';

    // Modelo con el que se ejecuta la acción: el que el director editorial le fijó
    // en el Gestor y, si no tiene, el que esté seleccionado en el chatbot.
    const modelAccion = getActionModel(action, selectedModel);

    // Para todas las acciones, mostrar el nombre del quick action y activar loader
    setIsLoading(true);
    setMessages(prev => [...prev, {
      role: 'user',
      content: `${action.label}`
    }]);

    try {
      // Consultar caché antes de llamar a la IA
      const responseType = getActionOutputType(action);
      let responseContent;
      let fromCache = false;
      // Caché de lectura: solo para acciones de arriba (las de chat nunca se cachean).
      if (!isChatAction && context.bookId) {
        const cacheEntry = await getChatbotCacheEntry(context.bookId, context.lessonId || null, action.actionId || action.id, userRole);
        // Ignorar entradas envenenadas: caché escrita antes de validar la respuesta
        // puede contener el mensaje de error de fallback —o un rechazo del modelo—
        // en vez del contenido real. Sin esto, una falla de una sola vez queda
        // servida para siempre a todos los usuarios de ese rol en ese libro.
        if (cacheEntry?.exists && cacheEntry?.data?.responseContent
          && !isChatbotErrorFallback(cacheEntry.data.responseContent)
          && !esRespuestaRechazada(cacheEntry.data.responseContent)) {
          responseContent = cacheEntry.data.responseContent;
          fromCache = true;
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      // Solo llamar a la IA y guardar si no hubo hit en caché
      if (!fromCache) {
        const umaId = selectedLeccion?.umaId || selectedLeccion?._id || lessonUMAData?._id;
        // Historial: solo las acciones del área de chat son conversacionales.
        const conversationHistory = isChatAction
          ? messages.filter(m => m.content && (m.role === 'user' || m.role === 'assistant'))
          : [];

        // Nunca responder con el contenido de otro libro. Si lo cargado no es del
        // libro abierto (se acaba de cambiar de libro y la recarga sigue en vuelo),
        // se espera a tenerlo antes de llamar a la IA.
        let datosLibro = bookData;
        let leccionesDelLibro = bookLeccionesData;
        if (context.bookId && bookLeccionesBookId !== context.bookId) {
          const recargado = await fetchBookLeccionesData(context.bookId);
          datosLibro = recargado?.bookData || null;
          leccionesDelLibro = recargado?.leccionesData || [];
        }

        const { content, metadata } = await executeQuickActionWithDocumentoIA({
          action,
          umaId,
          rol: userRole,
          chatRole,
          isBookView: context.isBookView,
          conversationHistory,
          lessonContext: selectedLeccion,
          lessonUMAData,
          bookData: datosLibro,
          leccionesData: leccionesDelLibro,
          model: modelAccion
        });
        responseContent = content;

        // Caché de escritura: solo para acciones de arriba y solo si la respuesta es
        // válida. Nunca cachear una respuesta degradada (fallback sin Documento IA) ni
        // el mensaje de error de sendChatMessage: quedarían fijos para todos los alumnos.
        // `truncado`/`incompleto`: la respuesta se cortó por el tope de salida del
        // modelo, falló un lote o no cubrió todas las lecciones. Se muestra (es mejor
        // que nada) pero no se cachea: el próximo intento puede salir completo.
        const motivoNoCachear =
          isChatAction                        ? 'es una acción del área de chat (nunca se cachean)'
          : !context.bookId                   ? 'no hay libro en el contexto'
          : !responseContent                  ? 'la respuesta vino vacía'
          : !metadata?.usedDocumentoIA        ? 'la respuesta salió del fallback, sin Documento IA'
          : metadata?.truncado                ? 'la respuesta se cortó por el tope de salida del modelo'
          : metadata?.incompleto              ? 'la respuesta no cubrió todas las lecciones'
          : isChatbotErrorFallback(responseContent) ? 'la respuesta es el mensaje de error del chat'
          : esRespuestaRechazada(responseContent)   ? 'el modelo rechazó la tarea'
          : null;

        if (!motivoNoCachear) {
          saveChatbotCacheEntry({
            bookId: context.bookId,
            lessonId: context.lessonId || null,
            quickActionId: action.actionId || action.id,
            rol: userRole,
            responseContent,
          }).catch(err => console.warn('[ChatbotLibro] No se pudo guardar en caché:', err?.message));
        } else if (!isChatAction) {
          // Sin esto, una respuesta que no se cachea es indistinguible de un fallo
          // del endpoint: no queda rastro de por qué no se guardó.
          console.warn(`[ChatbotLibro] Respuesta no cacheada: ${motivoNoCachear}`, {
            actionId: action.actionId || action.id, rol: userRole, metadata,
          });
        }
      }
      
      // Si la generación del guion falló, no intentar sintetizar el mensaje de error como audio/podcast
      if ((responseType === 'audio' || responseType === 'podcast') && isChatbotErrorFallback(responseContent)) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `${responseContent}\n\n*No se pudo generar el ${responseType === 'podcast' ? 'podcast' : 'audio'}. Por favor intenta de nuevo.*`
        }]);
        return;
      }

      // Si es acción de audio, manejar con audio
      if (responseType === 'audio') {
        // Agregar mensaje con estado de carga de audio (sin mostrar el texto)
        const messageId = Date.now();
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: '',  // No mostrar el texto del guion
          audioLoading: true,
          messageId,
          isAudio: true
        }]);

      // Determinar el estilo según el tipo de acción
      let audioStyle = 'motivacional'; // default

        // Generar el audio
        setAudioLoading(messageId);
        const audioUrl = await generateAudio(responseContent, 'alloy', audioStyle);
        
        // Actualizar el mensaje con el audio o mostrar error
        if (audioUrl && !audioUrl.error) {
          setMessages(prev => prev.map(msg => 
            msg.messageId === messageId 
              ? { ...msg, audioUrl, audioLoading: false }
              : msg
          ));
        } else {
          // Error al generar audio
          const errorMessage = audioUrl?.error 
            ? `*Error al generar audio: ${audioUrl.message}*\n\n${responseContent}`
            : `${responseContent}\n\n*No se pudo generar el audio. Por favor intenta de nuevo.*`;
          
          setMessages(prev => prev.map(msg => 
            msg.messageId === messageId 
              ? { 
                  ...msg, 
                  audioLoading: false, 
                  content: errorMessage
                }
              : msg
          ));
        }
        setAudioLoading(null);
      }
      else if (responseType === 'podcast') {
        // Acción de podcast - generar audio con múltiples voces
        const messageId = Date.now();
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: '',  // No mostrar el texto del guion
          audioLoading: true,
          messageId,
          isPodcast: true
        }]);
        
        // Generar podcast con el endpoint específico
        const podcastUrl = await generatePodcast(responseContent, {
          hostVoice: 'alloy',
          guestVoice: 'nova',
          hostSpeed: 1.25,
          guestSpeed: 1.25
        });
        
        if (podcastUrl && !podcastUrl.error) {
          // Actualizar mensaje con el audio del podcast
          setMessages(prev => prev.map(msg => 
            msg.messageId === messageId 
              ? { 
                  ...msg, 
                  audioLoading: false, 
                  audioUrl: podcastUrl,
                  isPodcast: true
                }
              : msg
          ));
        } else {
          // Error al generar podcast
          const errorMessage = podcastUrl?.error 
            ? `*Error al generar podcast: ${podcastUrl.message}*\n\n${responseContent}`
            : `${responseContent}\n\n*No se pudo generar el podcast. Por favor intenta de nuevo.*`;
          
          setMessages(prev => prev.map(msg => 
            msg.messageId === messageId 
              ? { 
                  ...msg, 
                  audioLoading: false, 
                  content: errorMessage
                }
              : msg
          ));
        }
        setAudioLoading(null);
      }
      else if (responseType === 'diagram') {
        // Flag explícito: generar diagrama. El tipo y la instrucción salen de action.query
        // (corto y claro: "Genera un mapa mental de la lección X"); el CONTENIDO fuente es
        // responseContent (Documento IA + PromptBuilder), que se pasa como contentOverride.
        const diagramType = detectDiagramType(action.query) || 'mindmap';

        const { code, error: codeError } = await generateDiagramCode(diagramType, action.query, lessonUMAData, bookData, modelAccion, responseContent);

        if (codeError || !code) {
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: 'No pude generar el diagrama. ' + (codeError || 'Intenta de nuevo.')
          }]);
          return;
        }

        if (diagramType === 'mindmap') {
          setMessages(prev => [...prev, { role: 'assistant', content: 'Generando mapa mental...', isLoading: true }]);
          const { data, error: parseError } = parseMindElixirData(code);
          if (parseError || !data) {
            setMessages(prev => prev.map(msg =>
              msg.isLoading ? { role: 'assistant', content: 'Error al procesar el mapa mental. ' + (parseError || ''), isLoading: false } : msg
            ));
            return;
          }
          setMessages(prev => prev.map(msg =>
            msg.isLoading ? { role: 'assistant', content: '¡He creado un mapa mental para ti!', diagram: { type: 'mindmap', mindmapData: data, code }, isLoading: false } : msg
          ));
        } else {
          const { svg, error: renderError } = await renderMermaid(code);
          if (renderError || !svg) {
            setMessages(prev => [...prev, { role: 'assistant', content: 'Error al renderizar el diagrama. ' + (renderError || '') }]);
            return;
          }
          setMessages(prev => [...prev, { role: 'assistant', content: '¡He creado un diagrama para ti!', diagram: { type: diagramType, svgContent: svg, code } }]);
        }
      }
      else if (responseType === 'image') {
        // Flag explícito: generar imagen. buildActionPrompt() ya generó la descripción
        // visual con Documento IA y las reglas de outputType = image; se usa directamente.
        setMessages(prev => [...prev, { role: 'assistant', content: 'Generando imagen...', isLoading: true }]);
        const imageUrl = await generateImage(responseContent);
        setMessages(prev => prev.map(msg =>
          msg.isLoading ? (imageUrl
            ? { role: 'assistant', content: '¡He creado una imagen para ti! Haz clic para descargarla.', image: imageUrl, isLoading: false }
            : { role: 'assistant', content: responseContent, isLoading: false }
          ) : msg
        ));
      }
      else if (responseType === 'structured') {
        // Flag explícito: salida estructurada. responseContent es JSON generado por
        // la IA (buildActionPrompt + schema del structuredType). Se parsea de forma
        // defensiva y se renderiza con formato; si falla, fallback a texto.
        const { data, error } = safeParseStructuredResponse(responseContent);
        if (data && !error) {
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: '',
            structured: data,
            structuredType: getStructuredType(action) || data.tipo || '',
          }]);
        } else {
          console.warn('[ChatbotLibro] No se pudo parsear la respuesta estructurada:', error);
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: responseContent || 'No se pudo generar el contenido. Por favor intenta de nuevo.',
          }]);
        }
      }
      else {
        // Respuesta de texto — el prompt del director no se analiza por keywords
        setMessages(prev => [...prev, { role: 'assistant', content: responseContent }]);
      }
    } catch (error) {
      console.error('[ChatbotLibro] Error en quick action:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Lo siento, hubo un error al procesar tu solicitud.' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClearChat = () => {
    const nombre = user?.nombres ? `${user.nombres}! ` : '';
    const titulo = bookData?.titulo || '';
    const saludo = titulo
      ? (userRole === 'ALUMNO'
          ? `¡Hola ${nombre}Soy tu asistente para estudiar el libro "${titulo}". ¿En qué puedo ayudarte?`
          : `¡Hola ${nombre}Soy tu asistente para consultas sobre el libro "${titulo}". ¿En qué puedo ayudarte?`)
      : '¡Hola! Soy tu asistente de contenido. ¿En qué puedo ayudarte?';
    setMessages([{ role: 'assistant', content: saludo }]);
  };

  const handleCopyLastResponse = async () => {
    const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant' && (m.content || m.structured));
    if (!lastAssistant) return;
    const text = lastAssistant.structured
      ? JSON.stringify(lastAssistant.structured, null, 2)
      : lastAssistant.content;
    if (!text) return;
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRegenerate = async () => {
    const lastUser = [...messages].reverse().find(m => m.role === 'user');
    if (!lastUser || isLoading) return;
    setMessages(prev => prev.filter((_, i) => i < prev.lastIndexOf(prev.find(m => m.role === 'user' && m.content === lastUser.content))));
    await handleSendMessage(lastUser.content);
  };

  const toggleRecording = () => {
    if (isRecording) {
      recognitionRef.current?.stop();
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Tu navegador no soporta reconocimiento de voz. Por favor usa Chrome o Edge.'
      }]);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'es-MX';
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onstart = () => setIsRecording(true);

    recognition.onresult = (event) => {
      let finalTranscript = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];

        if (result.isFinal) {
          finalTranscript += result[0].transcript;
        }
      }

      if (finalTranscript.trim()) {
        setInputValue(prev =>
          `${prev} ${finalTranscript}`.trim()
        );
      }
    };

    recognition.onend = () => {
      if (isRecording) {
        recognition.start();
      } else {
        setIsRecording(false);
        recognitionRef.current = null;
      }
    };

    recognition.onerror = () => {
      setIsRecording(false);
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  // Función para exportar a Excel
  const exportToExcel = (content, filename) => {
    try {
      // Parsear el contenido para estructurarlo
      const structuredData = parseContentForExcel(content);
      
      // Crear CSV
      const csvContent = createCSV(structuredData);
      
      // Crear blob y descargar
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `${filename}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
    }
  };

  // Función para parsear el contenido y estructurarlo
  const parseContentForExcel = (content) => {
    const lines = content.split('\n').filter(line => line.trim());
    const data = [];
    let currentSection = '';
    let currentTable = [];
    
    for (const line of lines) {
      // Detectar encabezados (líneas que empiezan con # o están en mayúsculas)
      if (line.startsWith('#') || line.startsWith('##') || /^[A-ZÁÉÍÓÚÑ]/.test(line.trim())) {
        // Guardar tabla anterior si existe
        if (currentTable.length > 0) {
          data.push({ section: currentSection, table: currentTable });
          currentTable = [];
        }
        currentSection = line.replace(/^#+\s*/, '').trim();
      } 
      // Detectar listas o puntos
      else if (line.startsWith('-') || line.startsWith('*') || line.match(/^\d+\./)) {
        const item = line.replace(/^[-*]\s*|^\d+\.\s*/, '').trim();
        currentTable.push({ Concepto: item, Descripción: '', Notas: '' });
      }
      // Detectar tablas implícitas (líneas con dos puntos o guiones)
      else if (line.includes(':') || line.includes('-')) {
        const parts = line.split(/[:\-]/).map(part => part.trim());
        if (parts.length >= 2) {
          currentTable.push({ 
            Concepto: parts[0], 
            Descripción: parts[1], 
            Notas: parts.slice(2).join(' ') 
          });
        }
      }
    }
    
    // Guardar última tabla si existe
    if (currentTable.length > 0) {
      data.push({ section: currentSection, table: currentTable });
    }
    
    return data;
  };

  // Función para crear CSV
  const createCSV = (structuredData) => {
    let csv = '';
    
    for (const section of structuredData) {
      // Agregar título de sección
      csv += `${section.section}\n\n`;
      
      // Agregar encabezados de tabla
      csv += 'Concepto,Descripción,Notas\n';
      
      // Agregar datos de la tabla
      for (const row of section.table) {
        csv += `"${row.Concepto}","${row.Descripción}","${row.Notas}"\n`;
      }
      
      csv += '\n';
    }
    
    return csv;
  };

  // No renderizar si el usuario no tiene acceso
  /* if (!hasAccess) {
    return null;
  } */

  if (shouldHide || !hayAccionesDelRol) return null;

  return (
    <div className="chatbot-container">
      {/* Panel de chat */}
      {isOpen && (
        <div className={`chatbot-panel ${isExpanded ? 'chatbot-panel-expanded' : ''} ${isClosing ? 'chatbot-panel-closing' : ''}`}>
          {/* Header */}
          <div className="chatbot-header">
            <div className="chatbot-header-actions">
              <button
                className="chatbot-expand"
                onClick={() => setIsExpanded(!isExpanded)}
                title={isExpanded ? "Minimizar" : "Expandir"}
              >
                {isExpanded ? <MinimizeIcon /> : <ExpandIcon />}
              </button>
              <button className="chatbot-close" onClick={() => {
                setIsClosing(true);
                setTimeout(() => { setIsOpen(false); setIsClosing(false); }, 440);
              }}>
                <CloseIcon />
              </button>
            </div>
            <div className="chatbot-header-info">
              <div className="chatbot-avatar">
                <ChatIcon />
              </div>
              <div className="chatbot-title">NIA</div>
              <div className="chatbot-subtitle">
                {selectedLeccion
                  ? `Lección: ${selectedLeccion.titulo || selectedLeccion._id}`
                  : 'Pregúntame sobre el contenido'}
              </div>
            </div>
          </div>

          {/* Quick Actions - barra superior (location === 'top') */}
          {!isLoading && topQuickActions.length > 0 && (
            <div className="chatbot-quick-actions-container">
              <div className="chatbot-quick-actions">
                {topQuickActions.map((action, index) => (
                  <button
                    key={action.id || index}
                    className={`chatbot-quick-action-btn ${action.placeholder ? 'chatbot-quick-action-placeholder' : ''}`}
                    onClick={() => handleQuickAction(action)}
                    title={action.placeholder ? 'Próximamente' : action.label}
                  >
                    {/* {getActionOutputType(action) === 'audio' && <span className="chatbot-audio-icon">🔊</span>} */}
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="chatbot-messages">
            {messages.map((msg, index) => (
              <div key={index} className={`chatbot-message ${msg.role} ${(msg.isAudio || msg.isPodcast) ? 'chatbot-message-audio' : ''}`}>
                <div className="chatbot-message-content">
                  {msg.content && (
                    msg.role === 'assistant'
                      ? <ReactMarkdown remarkPlugins={[remarkGfm]} className="chatbot-markdown">{msg.content}</ReactMarkdown>
                      : <span>{msg.content}</span>
                  )}
                  {msg.image && (
                    <img 
                      src={msg.image} 
                      alt="Visualización generada" 
                      className="chatbot-message-image"
                      onClick={() => {
                        // Descargar imagen en lugar de abrir en nueva pestaña
                        const link = document.createElement('a');
                        link.href = msg.image;
                        link.download = `mapa_mental_${Date.now()}.webp`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }}
                    />
                  )}
                  {msg.diagram && (
                    <DiagramRenderer
                      type={msg.diagram.type}
                      svgContent={msg.diagram.svgContent}
                      mindmapData={msg.diagram.mindmapData}
                      code={msg.diagram.code}
                    />
                  )}
                  {msg.structured && renderStructuredContent(msg.structured, msg.structuredType, userRole)}
                  {/* Audio Player */}
                  {msg.audioLoading && (
                    <div className="chatbot-audio-loading">
                      <span className="chatbot-audio-spinner"></span>
                      {msg.isPodcast ? 'Generando podcast...' : 'Generando audio...'}
                    </div>
                  )}
                  {msg.audioUrl && (
                    <div className="chatbot-audio-player">
                      <audio controls src={msg.audioUrl}>
                        Tu navegador no soporta el elemento de audio.
                      </audio>
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="chatbot-typing">
                <div className="chatbot-typing-dot"></div>
                <div className="chatbot-typing-dot"></div>
                <div className="chatbot-typing-dot"></div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input - Solo visible para PROFESOR */}
          {userRole === 'PROFESOR' && (
            <div className="chatbot-input-container">
              <div className="chatbot-input-wrapper">
                <div className="chatbot-input-row">
                  <textarea
                    ref={inputRef}
                    rows={1}
                    className="chatbot-input"
                    placeholder="Escribe tu pregunta..."
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyPress}
                    disabled={isLoading}
                  />
                  {inputValue.trim() ? (
                    <button
                      className="chatbot-send"
                      onClick={() => handleSendMessage()}
                      disabled={isLoading}
                      title="Enviar"
                    >
                      <SendIcon />
                    </button>
                  ) : (
                    <button
                      className={`chatbot-send chatbot-mic${isRecording ? ' chatbot-mic-recording' : ''}`}
                      onClick={toggleRecording}
                      disabled={isLoading}
                      title={isRecording ? 'Detener grabación' : 'Grabar voz'}
                    >
                      {isRecording ? <StopRecordingIcon /> : <MicrophoneIcon />}
                    </button>
                  )}
                </div>
                <div className="chatbot-input-toolbar">
                  <button className="chatbot-toolbar-btn" onClick={handleClearChat} title="Limpiar chat">
                    <TrashIcon />
                    <span>Limpiar</span>
                  </button>
                  <button className="chatbot-toolbar-btn" onClick={handleCopyLastResponse} title="Copiar última respuesta">
                    <ClipboardIcon />
                    <span>{copied ? 'Copiado' : 'Copiar'}</span>
                  </button>
                  {categoryGroups.length > 0 && (
                    <CategoriesMenu groups={categoryGroups} onPick={handleQuickAction} />
                  )}
                  {/* <button className="chatbot-toolbar-btn" onClick={handleRegenerate} disabled={isLoading} title="Regenerar última respuesta">
                    <RegenerateIcon />
                    <span>Regenerar</span>
                  </button> */}
                  <ModelDropdown value={selectedModel} onChange={setSelectedModel} />
                </div>
              </div>
            </div>
          )}

          {/* Alumno (no PROFESOR): solo el botón de Categorías (si aplica a su rol),
              sin input ni Limpiar/Copiar/selector de modelo. */}
          {userRole !== 'PROFESOR' && categoryGroups.length > 0 && (
            <div className="chatbot-input-container chatbot-categories-only">
              <CategoriesMenu groups={categoryGroups} onPick={handleQuickAction} />
            </div>
          )}
        </div>
      )}

      {/* Botón flotante */}
      {!isOpen && (
        <div className="chatbot-button-wrapper">
          <button className="chatbot-button" onClick={() => setIsOpen(true)}>
            <ChatIcon />
          </button>
        </div>
      )}
    </div>
  );
};

export default ChatbotLibro;
